import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from 'react-helmet-async';
import Footer from "../components/Footer";
import "./urimaidetails.css";
import { useNavigate } from "react-router-dom";

// Importing images from Urimai Img
import mainLogo from "../Urimai Img/main.png";
import img2 from "../Urimai Img/2 3.png";
import img4 from "../Urimai Img/4 5.png";
import img6 from "../Urimai Img/6 2.png";
import img9 from "../Urimai Img/9 2.png";

const faqs = [
  {
    question: "What is Urimai?",
    answer: "Urimai is a civic technology mobile application that provides transparent access to election candidate information based on official affidavits filed with the Election Commission of India."
  },
  {
    question: "What information does Urimai provide?",
    answer: "Urimai provides candidate profiles including personal information, party affiliation, financial assets, liabilities, education details, and criminal records declared in election affidavits."
  },
  {
    question: "Does Urimai support multiple languages?",
    answer: "Yes. Urimai supports both Tamil and English to make election data accessible to a wider audience."
  },
  {
    question: "Is the data in Urimai verified?",
    answer: "The information displayed in Urimai is derived from official election affidavit data submitted to the Election Commission of India."
  },
  {
    question: "Who can use Urimai?",
    answer: "Urimai is designed for citizens, voters, researchers, and anyone interested in understanding election candidates and public political data."
  }
];

const features = [
  {
    title: "Smart Candidate Search",
    desc: "Quickly find election candidates by constituency, name, or political party. Urimai simplifies large volumes of affidavit data into a searchable format.",
    icon: "fa-search"
  },
  {
    title: "Verified Candidate Profiles",
    desc: "Access detailed candidate profiles including official personal details, declared assets, liabilities, and educational qualifications.",
    icon: "fa-check-circle"
  },
  {
    title: "Bilingual Accessibility",
    desc: "Urimai supports both Tamil and English, making vital election data accessible to a broader audience.",
    icon: "fa-language"
  },
  {
    title: "Real-Time Data Integration",
    desc: "The app integrates directly with backend APIs to fetch and display updated election information securely.",
    icon: "fa-database"
  }
];

const UrimaiDetails = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const navigate = useNavigate();

  const heroImages = [img4, img6];

  // Detect iOS Safari to fix extra spacing around carousel
  // const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Click outside to close FAQ
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeFaq !== null && !event.target.closest('.ud-faq-item')) {
        setActiveFaq(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeFaq]);

  const isTabletRange = windowWidth >= 700 && windowWidth <= 900;

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleTalkToExpert = () => {
    navigate('/contact');
  };

  const handleGetThisTool = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/android/i.test(userAgent)) {
      window.open('https://play.google.com/store/apps/details?id=com.urimaiapp', '_blank');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      window.open('https://apps.apple.com/in/app/urimai/id6475168659', '_blank');
    } else {
      // Default to Android store for desktop/other devices
      window.open('https://play.google.com/store/apps/details?id=com.urimaiapp', '_blank');
    }
  };

  const handleGetAppCustomised = () => {
    window.location.href = 'mailto:info@ak-tech.in';
  };

  return (
    <div style={{ overflowX: 'hidden', position: 'relative' }}>
      <Helmet>
        <title>Urimai - Civic Transparency Platform | AK Consultants</title>
        <meta name="description" content="Urimai is a civic technology platform designed to make election information accessible, transparent, and easy to understand." />
      </Helmet>

      {/* Hero Section */}
      <section className="ud-hero" data-aos="fade-up">
        <div className="ud-hero-content">
          <div className="ud-hero-text">
            <div className="ud-logo-wrapper" style={{
              display: 'flex',
              flexDirection: isTabletRange ? 'row' : undefined,
              alignItems: isTabletRange ? 'center' : undefined,
              justifyContent: isTabletRange ? 'flex-start' : undefined,
              gap: '20px',
              marginBottom: '24px'
            }}>
              <img src={mainLogo} alt="Urimai Logo" className="ud-main-logo" />
              <div className="ud-title-section" style={{
                alignItems: isTabletRange ? 'flex-start' : undefined
              }}>
                <h1 className="ud-title" style={{
                  textAlign: isTabletRange ? 'left' : undefined
                }}>உரிமை</h1>
                <span className="ud-badge">NEW</span>
              </div>
            </div>
            
            <span className="ud-subtitle" style={{
              color: '#6b7280',
              fontSize: '20px',
              fontWeight: '600',
              marginBottom: '24px',
              lineHeight: '1.4',
              display: 'block',
              pointerEvents: 'none',
              textAlign: isTabletRange ? 'left' : 'center'
            }}>Civic Transparency Platform for Informed Voting</span>
            
            <ul className="ud-checklist">
              <li><i className="fa fa-check text-green"></i> Verified Candidate Profiles</li>
              <li><i className="fa fa-check text-green"></i> Smart Candidate Search</li>
              <li><i className="fa fa-check text-green"></i> Bilingual Accessibility (English & Tamil)</li>
              <li><i className="fa fa-check text-green"></i> Financial & Criminal Record Analysis</li>
              <li><i className="fa fa-check text-green"></i> Real-Time Data Integration</li>
              <li><i className="fa fa-check text-green"></i> Transparent Public Information</li>
            </ul>

            <div className="ud-hero-buttons" style={{
              display: 'flex',
              gap: '16px',
              marginBottom: isTabletRange ? '50px' : '30px',
              justifyContent: isTabletRange ? 'center' : undefined
            }}>
              <button className="ud-btn-primary" onClick={handleTalkToExpert}>Talk To Our Expert</button>
              <button className="ud-btn-outline" onClick={handleGetThisTool}>Get This Tool</button>
            </div>
          </div>

          {/* Desktop carousel — hidden on mobile via CSS */}
          <div className="ud-hero-visuals ud-hero-visuals-desktop" style={{
            paddingTop: isTabletRange ? '40px' : undefined,
          }}>
            <div className="ud-visual-container">
              <div className={`ud-image-card ${currentImageIndex === 0 ? 'front' : 'back'}`}>
                <img src={heroImages[0]} alt="Urimai App 1" className="ud-mockup ud-hero-image" />
              </div>
              <div className={`ud-image-card ${currentImageIndex === 1 ? 'front' : 'back'}`}>
                <img src={heroImages[1]} alt="Urimai App 2" className="ud-mockup ud-hero-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile carousel — rendered outside hero so no padding/height hacks needed */}
      <div className="ud-hero-visuals-mobile">
        <div className="ud-visual-container">
          <div className={`ud-image-card ${currentImageIndex === 0 ? 'front' : 'back'}`}>
            <img src={heroImages[0]} alt="Urimai App 1" className="ud-mockup ud-hero-image" />
          </div>
          <div className={`ud-image-card ${currentImageIndex === 1 ? 'front' : 'back'}`}>
            <img src={heroImages[1]} alt="Urimai App 2" className="ud-mockup ud-hero-image" />
          </div>
        </div>
      </div>

      {/* Overview Intro */}
      <section className="ud-overview-intro" data-aos="fade-up">
        <div className="ud-container">
          <h2 className="ud-centered-title">Bringing Transparency to Democracy</h2>
          <p className="ud-centered-desc">
            Urimai is a civic technology platform designed to make election information accessible, 
            transparent, and easy to understand. By transforming complex election affidavits into 
            structured and searchable insights, Urimai empowers citizens to evaluate candidates 
            based on verified public data. Built using modern mobile technologies and intelligent 
            data processing, Urimai allows users to explore candidate profiles, analyze financial and 
            criminal records, and understand election information with clarity.
          </p>
        </div>
      </section>

      {/* 4-Column Features */}
      <section className="ud-features-grid" data-aos="fade-up">
        <div className="ud-container">
          <div className="ud-grid-4">
            {features.map((feature, idx) => (
              <div className="ud-feature-card" key={idx}>
                <div className="ud-feature-icon">
                  <i className={`fa ${feature.icon}`}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Overview Split Section */}
      <section className="ud-platform-split" data-aos="fade-up">
        <div className="ud-container ud-split-layout">
          <div className="ud-split-text">
            <h2>Transparent Information from Industry Experts</h2>
            <p><strong>Promoting transparent and informed voting</strong></p>
            <p>
              Urimai transforms raw election affidavit documents into an easy-to-understand digital 
              platform that promotes transparency and accountability in elections. Instead of navigating 
              lengthy documents and fragmented data sources, citizens can access consolidated candidate 
              insights in one place.
            </p>
            <p>
              The platform provides structured data visualization, quick candidate lookup, and a user-friendly 
              interface designed to simplify civic engagement. Urimai bridges the gap between public 
              election records and everyday voters by turning complex information into accessible knowledge.
            </p>
            
            <ul className="ud-why-list">
              <li>Simplifies thousands of election affidavits</li>
              <li>Provides structured candidate insights</li>
              <li>Fast and responsive mobile experience</li>
            </ul>

            <button className="ud-btn-blue" onClick={handleGetAppCustomised}>Get Your App Customised</button>
          </div>
          
          <div className="ud-split-visuals">
            <img src={img2} alt="Urimai App 1" className="ud-tilt-img ud-img-front" />
            <img src={img9} alt="Urimai App 2" className="ud-tilt-img ud-img-back" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="ud-faq-section" data-aos="fade-up">
        <div className="ud-circle-bottom-right-1"></div>
        <div className="ud-circle-bottom-right-2"></div>
        <div className="ud-container">
          <h2>Frequently Asked Questions (FAQ)</h2>
          <div className="ud-faq-list">
            {faqs.map((faq, idx) => (
              <div 
                className={`ud-faq-item ${activeFaq === idx ? 'active' : ''}`} 
                key={idx}
                onClick={() => toggleFaq(idx)}
              >
                <div className="ud-faq-question">
                  <span>{faq.question}</span>
                  <i className={`fa fa-chevron-${activeFaq === idx ? 'up' : 'down'}`}></i>
                </div>
                {activeFaq === idx && (
                  <div className="ud-faq-answer">
                    <div style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      padding: '20px',
                      margin: '16px 20px 0 20px'
                    }}>
                      <p style={{
                        color: '#000000',
                        fontSize: '18px',
                        fontWeight: '600',
                        lineHeight: '1.7',
                        margin: '0'
                      }}>{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>



      <Footer />
    </div>
  );
};

export default UrimaiDetails;
